import { filter } from 'lodash';
import { useState, useEffect } from 'react';
import { Switch, Tooltip } from '@mui/material';

// redux
import { useDispatch, useSelector } from 'src/redux/store';
import { getListaUsuariosByClienteId } from 'src/redux/slices/user';

// actions
import actionEditarSenha from 'src/actions/usuarios/actionEditarSenhaUsuario';
import actionEditarPerfil from 'src/actions/usuarios/actionEditarPerfil';

// hooks
import useAuth from 'src/hooks/useAuth';

// components
import ModalEditarAgente from '../../cadastro/ModalEditarAgente';
import ModalEditarSenhaAgente from '../../cadastro/ModalEditarSenhaAgente';
import GenericTable from '../../../../components/table/GenericTable';
import { GENERIC_TABLE_DESIGN } from '../../../../components/table/GenericTable.design';
import { Avatar, AvatarImage, AvatarFallback } from 'src/@/components/ui/avatar';
import { USUARIOS_COMPONENT_DESIGN } from './UsuariosComponent.design';

// utils
import { fDateTime } from 'src/utils/formatTime';
import {
    User,
    Phone,
    Mail,
    Clock,
    Shield,
    Key,
    Edit2,
    Users
} from 'lucide-react';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
    { id: 'nome', label: 'Nome', className: 'flex-1 min-w-[200px] pr-4' },
    { id: 'setor', label: 'Setor', className: 'w-32 hidden md:block' },
    { id: 'ramal', label: 'Ramal', className: 'w-20 hidden md:block' },
    { id: 'email', label: 'Email', className: 'w-48 hidden lg:block' },
    { id: 'standby', label: 'Intervalo', className: 'w-24 text-center' },
    { id: 'last_online', label: 'Última Ativ.', className: 'w-32 text-right' },
    { id: 'status', label: 'Status', className: 'w-24 text-center' },
    { id: 'actions', label: 'Ações', className: 'w-24 text-center' },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    if (query) {
        return filter(array, (_user) => _user.nomeFantasia.toLowerCase().indexOf(query.toLowerCase()) !== -1);
    }
    return stabilizedThis.map((el) => el[0]);
}

export default function UsuariosComponent({ user }) {
    const dispatch = useDispatch();
    const { usuariosList } = useSelector((state) => state.user);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('nome');
    const [filterName, setFilterName] = useState('');
    const [openEditarSenha, setOpenEditarSenha] = useState(false);
    const [openEditarPerfil, setOpenEditarPerfil] = useState(false);
    const [senha, setSenha] = useState('');
    const [confirmarSenha, setConfirmarSenha] = useState('');
    const [usuarioId, setUsuarioId] = useState('');
    const [dataUsuario, setDataUsuario] = useState(null)

    useEffect(() => {
        dispatch(getListaUsuariosByClienteId(user.clienteId, '1'));
    }, []);

    const handleOpenDialogEditarPerfil = (usuario) => {
        setDataUsuario(usuario);
        setOpenEditarPerfil(true)
    }

    const handleOpenDialogEditarSenha = (data) => {
        setDataUsuario(data)
        setOpenEditarSenha(true)
    }

    const resetarCampos = () => {
        setUsuarioId('');
        setSenha('');
        setConfirmarSenha('');
    }

    async function handleEditStatus(usuarioId, status) {
        try {
            await actionEditarPerfil({
                usuarioId: usuarioId,
                status: status,
            });
            dispatch(getListaUsuariosByClienteId(user.clienteId, '1'));
        } catch (error) {
            console.log('error', error);
        }
    }

    const filteredUsers = applySortFilter(usuariosList, getComparator(order, orderBy), filterName);

    return (
        <div className={USUARIOS_COMPONENT_DESIGN.page.container}>
            {/* Header */}
            <div className={USUARIOS_COMPONENT_DESIGN.page.header.container}>
                <div>
                    <h1 className={USUARIOS_COMPONENT_DESIGN.page.header.title}>Usuários</h1>
                    <p className={USUARIOS_COMPONENT_DESIGN.page.header.subtitle}>
                        O recurso usuários contém todos os agentes que sua empresa está envolvida. É possível editar usuários e senhas de acesso.
                    </p>
                </div>
                {/* 
                  Note: The original design had no 'Add User' button in the HeaderPageComponentConfig actions prop 
                  (it was commented out). If needed, it can be added here.
                */}
            </div>

            <ModalEditarSenhaAgente
                open={openEditarSenha}
                dataUsuario={dataUsuario}
                onCallback={() => setOpenEditarSenha(false)}
                onClose={() => setOpenEditarSenha(false)}
                callbackEdit={() => {
                    setOpenEditarSenha(false);
                    resetarCampos();
                }}
            />

            <ModalEditarAgente
                user={user}
                open={openEditarPerfil}
                onClose={() => setOpenEditarPerfil(false)}
                dataUsuario={dataUsuario}
                callbackEdit={() => {
                    setOpenEditarPerfil(false);
                    setDataUsuario(null);
                    dispatch(getListaUsuariosByClienteId(user.clienteId));
                }}
            />

            {/* List */}
            <div className="flex flex-col gap-4">
                {filteredUsers.length === 0 ? (
                    <div className={USUARIOS_COMPONENT_DESIGN.emptyState.container}>
                        <div className={USUARIOS_COMPONENT_DESIGN.emptyState.iconBox}>
                            <Users size={32} />
                        </div>
                        <h3 className={USUARIOS_COMPONENT_DESIGN.emptyState.title}>Nenhum usuário encontrado</h3>
                        <p className={USUARIOS_COMPONENT_DESIGN.emptyState.description}>
                            Não há usuários cadastrados ou correspondentes à busca.
                        </p>
                    </div>
                ) : (
                    <GenericTable headLabels={TABLE_HEAD}>
                        {filteredUsers.map((row) => (
                            <div
                                key={row._id}
                                className={GENERIC_TABLE_DESIGN.row.container}
                            >
                                {/* Nome */}
                                <div className={GENERIC_TABLE_DESIGN.common.colMain}>
                                    <div className="flex items-center gap-3">
                                        <Avatar className="h-9 w-9">
                                            <AvatarImage src={row.avatar} alt={row.nomeFantasia} />
                                            <AvatarFallback className="bg-violet-100 text-violet-700 text-sm font-semibold">
                                                {(row.nomeFantasia || '?').charAt(0).toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-900">{row.nomeFantasia}</h3>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className="text-[10px] text-gray-400">ID: {row._id ? row._id.substring(0, 6) : 'N/A'}...</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Setor */}
                                <div className="w-32 hidden md:block text-sm text-gray-600 truncate">
                                    {row.nome_setor || '-'}
                                </div>

                                {/* Ramal */}
                                <div className="w-20 hidden md:block">
                                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                                        <Phone size={14} className="text-gray-400" />
                                        <span>{row.ramal || '-'}</span>
                                    </div>
                                </div>

                                {/* Email */}
                                <div className="w-48 hidden lg:block px-2">
                                    <div className="flex items-center gap-1.5 text-sm text-gray-600 truncate" title={row.email}>
                                        <Mail size={14} className="text-gray-400 flex-shrink-0" />
                                        <span className="truncate">{row.email}</span>
                                    </div>
                                </div>

                                {/* Intervalo / Standby */}
                                <div className="w-24 text-center">
                                    <span className={`
                                        inline-flex items-center px-2 py-1 rounded-md text-xs font-medium
                                        ${row.userStandby
                                            ? 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20'
                                            : 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/10'}
                                    `}>
                                        {row.userStandby ? 'Sim' : 'Não'}
                                    </span>
                                </div>

                                {/* Ultima Atividade */}
                                <div className="w-32 text-right">
                                    <div className="flex flex-col items-end gap-0.5">
                                        <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                            <Clock size={12} className="text-gray-400" />
                                            <span>{fDateTime(row.lastOnline)}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Status */}
                                <div className="w-24 flex justify-center">
                                    <Switch
                                        checked={row.status}
                                        onChange={(e) => handleEditStatus(row._id, e.target.checked)}
                                        color="primary"
                                        size="small"
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#7C3AED',
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#7C3AED',
                                            },
                                        }}
                                    />
                                </div>

                                {/* Actions */}
                                <div className="w-24 flex justify-center items-center gap-1">
                                    <Tooltip title="Alterar Senha">
                                        <button
                                            onClick={() => handleOpenDialogEditarSenha(row)}
                                            className="p-1.5 text-gray-400 hover:text-[#7C3AED] hover:bg-[#7C3AED]/5 rounded-lg transition-colors"
                                        >
                                            <Shield size={16} />
                                        </button>
                                    </Tooltip>

                                    <Tooltip title="Editar Usuário">
                                        <button
                                            onClick={() => handleOpenDialogEditarPerfil(row)}
                                            className="p-1.5 text-gray-400 hover:text-[#7C3AED] hover:bg-[#7C3AED]/5 rounded-lg transition-colors"
                                        >
                                            <Edit2 size={16} />
                                        </button>
                                    </Tooltip>
                                </div>
                            </div>
                        ))}
                    </GenericTable>
                )}
            </div>
        </div>
    );
}
